import React from "react";
import { jsPDF } from "jspdf";
import { useAppSelector } from "redux/hooks";
import { LoadingButton } from "@mui/lab";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useState } from "react";
import headerLogoBase64 from "components/print/templates/headerlogo";
import { compileFinancialsTemplate } from "components/print/compilers/financials";
import { compilePropertyImagesTemplate } from "components/print/compilers/propertyImages";
import { compileSiteStackTemplate } from "components/print/compilers/siteStackPlanImages";
import { compileSummaryTemplate } from "components/print/compilers/summary";
import { compileRecommendationTemplate } from "components/print/compilers/recommendations";
import { compileSwotTemplate } from "components/print/compilers/swot";
import { compileSwotGlobalTemplate } from "components/print/compilers/swotGlobal";
import { compileKeyProvisionTemplate } from "components/print/compilers/keyProvisions";
import { compileKeyProvisionInternationalTemplate } from "components/print/compilers/keyProvisionsInternational";
import { compilePostClosingTemplate } from "components/print/compilers/postClosing";
import { compileValuationTemplate } from "components/print/compilers/valuation";
import { compileLeasingTemplate } from "components/print/compilers/leasing";
import { compileCapitalPlanTemplate } from "components/print/compilers/capitalPlan";
import { fetchAssetPlanData } from "components/print/util/printapiCallhelper";
import moment from "moment";
import { useAppDispatch } from "../../redux/hooks";
import {
  rejectionStatus,
  rejectionToast,
} from "../../redux/slices/dialogFormSlice";
import { compileDebtTemplate } from "./compilers/debt";
import { sustainabilityTemplate } from "./compilers/sustainability";
import { compileComplianceTemplate } from "./compilers/compliance";
import { compilePrioritiesTemplate } from "./compilers/priorities";

const PrintToPDF: React.FC = () => {
  const dispatch = useAppDispatch();
  const [printLoaderButton, setPrintLoaderButton] = useState(false);
  const investmentId =
    useAppSelector((state) => state.summary?.dapData?.investmentId) || "";
  const propertyType =
    useAppSelector((state) => state.summary?.dapData?.propertyType) || "";

  const budgetYear =
    useAppSelector((state) => state.summary?.dapData?.budgetYear) || "";
  const assetPlanId =
    useAppSelector((state) => state.summary?.dapData?.assetPlanId) || "";
  const investmentName =
    useAppSelector((state) => state.summary?.dapData?.dealName) || "";
  const internationalInd =
    useAppSelector((state) => state.summary?.dapData?.internationalInd) || "";
  

  const margins = { top: 70, right: 40, bottom: 40, left: 40 };

  const handlePrint = () => {
    setPrintLoaderButton(true);
    generateDapPdf();
  };

  const addSectionToPdf = (
    index,
    htmlContents,
    pdfInstance,
    pageHeight,
    width,
    assetPlanDetails
  ) => {
    const htmlContent = htmlContents[index];
    pdfInstance.html(htmlContent, {
      callback: (doc) => {
        index++;
        if (index < htmlContents.length) {
          addSectionToPdf(
            index,
            htmlContents,
            doc,
            pageHeight,
            width,
            assetPlanDetails
          );
        } else {
          generateFinalPdf(doc, assetPlanDetails);
        }
      },
      x: 0,
      y:
        index === 0
          ? 0
          : getPositionY(pdfInstance.internal.pages.length, pageHeight),
      autoPaging: htmlContent.includes('Physical Risk Consideration') ? "slice" : "text",
      width: width - (margins.left + margins.right), //target width in the PDF document
      windowWidth: width, //window width in CSS pixels
      margin: Object.values(margins),
    });
  };

  const generateDapPdf = async () => {
    var pdfInstance = new jsPDF("p", "pt");
    var width = pdfInstance.internal.pageSize.getWidth();

    const assetPlanDetails = {
      assetPlanId: assetPlanId,
      investmentId: investmentId,
      budgetYear: budgetYear,
      investmentName: investmentName,
      propertyType: propertyType,
      internationalInd: internationalInd

    };

    fetchAssetPlanData(assetPlanDetails).then((assetPlanData) => {
      console.log("fetchAssetPlanData",assetPlanData);
      
      if (assetPlanData.error) {
        setPrintLoaderButton(false);
        dispatch(rejectionToast(true));
        dispatch(
          rejectionStatus({
            message: "Export to pdf failed. Please try again!",
            severity: "error",
          })
        );
        setTimeout(() => {
          dispatch(rejectionToast(false));
        }, 4000);
      } else {
        const htmlContents = getHtmlContents(assetPlanData);
        const pageHeight = pdfInstance.internal.pageSize.getHeight();
        addSectionToPdf(
          0,
          htmlContents,
          pdfInstance,
          pageHeight,
          width,
          assetPlanDetails
        );
      }
    });
  };

  //for escape element inline style
  const escapeInlineStyle = (str) => {
    let finalStr = str.replaceAll("style=", "");
    return finalStr;
  };

  const getHtmlContents = (assetPlanData) => {
    const sections: string[] = [];

    // Summary
    console.log(assetPlanData , "krishna")
    if (assetPlanData.summaryData) {
      const summaryHtml = compileSummaryTemplate(assetPlanData.summaryData, assetPlanData.comments);
      if (summaryHtml) sections.push(escapeInlineStyle(summaryHtml));
    }

    // Property Images
    if (assetPlanData.propertyImages?.length) {
      const propertyImagesHtml = compilePropertyImagesTemplate(assetPlanData.propertyImages);
      if (propertyImagesHtml?.length) sections.push(...propertyImagesHtml);
    }

    // Recommendations
    if (assetPlanData.recomData?.length || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'missionstatement')) {
      const recommendationHtml = compileRecommendationTemplate(assetPlanData.recomData, assetPlanData.comments, parseInt(budgetYear), internationalInd);
      if (recommendationHtml) sections.push(escapeInlineStyle(recommendationHtml));
    }

    // Priorities (only for international)
    if (internationalInd === "Y" && assetPlanData.prioritiesData && Object.keys(assetPlanData.prioritiesData).length > 0) {
      const prioritiesHtml = compilePrioritiesTemplate(assetPlanData.prioritiesData);
      if (prioritiesHtml) sections.push(escapeInlineStyle(prioritiesHtml));
    }

    // Compliance
    if (assetPlanData.complianceData && Object.keys(assetPlanData.complianceData).length > 0) {
      const complianceHtml = compileComplianceTemplate(assetPlanData.complianceData);
      if (complianceHtml) sections.push(escapeInlineStyle(complianceHtml));
    }

    // Sustainability
    if (assetPlanData.sustainabilityData && Object.keys(assetPlanData.sustainabilityData).length > 0) {
      const sustainabilityData = sustainabilityTemplate(assetPlanData.sustainabilityData);
      if (sustainabilityData?.length) sections.push(...sustainabilityData.map(template => escapeInlineStyle(template)));
    }

    // Site Plans
    if (assetPlanData.sitePlans?.length) {
      const sitePlanHtml = compileSiteStackTemplate(assetPlanData.sitePlans, "SitePlan");
      if (sitePlanHtml?.length) sections.push(...sitePlanHtml);
    }

    // Stacking Plans
    if (assetPlanData.stackingPlans?.length) {
      const stackingPlanHtml = compileSiteStackTemplate(assetPlanData.stackingPlans, "StackingPlan");
      if (stackingPlanHtml?.length) sections.push(...stackingPlanHtml);
    }

    // Financials
    if ((assetPlanData.financials && Object.keys(assetPlanData.financials).length > 0) || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'financials')) {
      const financialsHtml = compileFinancialsTemplate(assetPlanData.financials, budgetYear, assetPlanData.comments, assetPlanData.summaryData?.rentableArea);
      if (financialsHtml) sections.push(financialsHtml);
    }

    // Capital Plan
    if ((assetPlanData.capitalPlanTable1?.length || assetPlanData.capitalPlanTable2?.length) || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'capitalplan')) {
      const capitalPlanHtml = compileCapitalPlanTemplate(assetPlanData.capitalPlanTable1, assetPlanData.capitalPlanTable2, budgetYear, assetPlanData.comments, assetPlanData.summaryData?.rentableArea);
      if (capitalPlanHtml) sections.push(capitalPlanHtml);
    }

    // SWOT
    if (assetPlanData.comments?.length && assetPlanData.comments.some(comment => comment.sectionName?.toLowerCase() === 'swot')) {
      const swotHtml = internationalInd === "Y" 
        ? compileSwotGlobalTemplate(assetPlanData.comments)
        : compileSwotTemplate(assetPlanData.comments);
      if (swotHtml) sections.push(escapeInlineStyle(swotHtml));
    }

    // Leasing
    if ((assetPlanData.residential.data || assetPlanData.leaseExpirations.data || assetPlanData.commertial.data || assetPlanData.keyTenants.data) || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'leasing')) {
      const leasingHtml = compileLeasingTemplate(assetPlanData.residential, assetPlanData.leaseExpirations, budgetYear, assetPlanData.commertial, assetPlanData.keyTenants, assetPlanData.comments, propertyType);
      if (leasingHtml?.length) {
        const escapedLeasingHtmlArray = leasingHtml.map((html: string) => escapeInlineStyle(html));
        sections.push(...escapedLeasingHtmlArray);
      }
    }

    // Valuation
    if (assetPlanData.valuationData?.length || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'valuation')) {
      const valuationHtml = compileValuationTemplate(assetPlanData.valuationData, propertyType, assetPlanData.comments, internationalInd);
      if (valuationHtml) sections.push(escapeInlineStyle(valuationHtml));
    }

    // Debt
    if (assetPlanData.debtData && Object.keys(assetPlanData.debtData).length > 1 && assetPlanData.debtData.assetPlanId !== '00000000-0000-0000-0000-000000000000') {
      const debtHtml = compileDebtTemplate(assetPlanData.debtData);
      if (debtHtml) sections.push(escapeInlineStyle(debtHtml));
    }
    
    //Key Provisions
    if ((assetPlanData.keyProvisionData && assetPlanData.keyProvisionData.assetPlanId !== '00000000-0000-0000-0000-000000000000') || assetPlanData.comments?.some(comment => comment.sectionName?.toLowerCase() === 'keyprovisions')) {
      const keyProvisionHtml = internationalInd === "Y" 
        ? compileKeyProvisionInternationalTemplate(assetPlanData.comments, assetPlanData.keyProvisionData)
        : compileKeyProvisionTemplate(assetPlanData.comments, assetPlanData.keyProvisionData, assetPlanData.propertyType , assetPlanData.JVData);
      console.log('Key Provisions HTML generated:', !!keyProvisionHtml, keyProvisionHtml?.length);
      if (keyProvisionHtml) sections.push(escapeInlineStyle(keyProvisionHtml));
    }

    // Post Closing
    if (assetPlanData.comments?.length && assetPlanData.comments.some(comment => comment.sectionName?.toLowerCase() === 'postclosing')) {
      const postClosingHtml = compilePostClosingTemplate(assetPlanData.comments);
      if (postClosingHtml) sections.push(escapeInlineStyle(postClosingHtml));
    }

    return sections;
  };

  const getPositionY = (numberOfPages: number, pageHeight: number) => {
    console.log(numberOfPages, pageHeight);
    const positionY =
      (numberOfPages - 1) * (pageHeight - (margins.top + margins.bottom));
    console.log(positionY);
    return positionY;
  };

  const generateFinalPdf = (finalDoc, assetPlanDetails) => {
    addHeaderFooters(finalDoc, assetPlanDetails);
    finalDoc.save(getPdfFileName(assetPlanDetails));
    setPrintLoaderButton(false);
  };

  const getPdfFileName = (assetPlanDetails) => {
    const currentTime = new Date();
    return `DAP ${assetPlanDetails.budgetYear} - ${assetPlanDetails.investmentId
      } - ${moment().format("MMM-DD-YYYY hhmm A")}.pdf`;
  };

  const addHeaderFooters = (doc, assetPlanDetails) => {
    const pageCount = doc.internal.getNumberOfPages();
    console.log(doc.internal.pageSize.width, doc.internal.pageSize.height);
    for (var i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addHeader(doc, assetPlanDetails);
      doc.setFontSize(8);
      doc.setTextColor("black");
      doc.text(
        "Page " + String(i) + " of " + String(pageCount),
        doc.internal.pageSize.width / 2,
        820,
        {
          align: "center",
        }
      );
      // doc.text('HEADER TEMPLATE', doc.internal.pageSize.width / 2, 20, {
      //   align: 'center'
      // });
    }
  };

  const addHeader = (doc, assetPlanDetails) => {
    if (headerLogoBase64) {
      doc.addImage(headerLogoBase64, "PNG", margins.left, 10, 80, 30);
    }
    doc.setFontSize(16);
    doc.setTextColor("green");
    doc.text(
      `${assetPlanDetails.budgetYear} Digital Asset Plan`,
      margins.left + 90,
      25,
      { align: "left" }
    );
    doc.setFontSize(12);
    doc.text(
      `${assetPlanDetails.investmentId} - ${assetPlanDetails.investmentName}`,
      margins.left + 90,
      40,
      { align: "left" }
    );
  };

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        whiteSpace: "nowrap",
      }}
    >
      <LoadingButton
        loading={printLoaderButton}
        variant="contained"
        onClick={handlePrint}
        className="primary"
        size="small"
        sx={{ lineHeight: 2.2 }}
      >
        <PictureAsPdfIcon sx={{ mr: "2.5px" }} />
        Export to PDF
      </LoadingButton>
    </div>
  );
};

export default PrintToPDF;
